
import { useState } from 'react';

interface IngredientFilterProps {
  allIngredients: string[];
  includedIngredients: string[];
  excludedIngredients: string[];
  onIncludedChange: (ingredients: string[]) => void;
  onExcludedChange: (ingredients: string[]) => void;
  onClear: () => void;
}

export default function IngredientFilter({
  allIngredients,
  includedIngredients,
  excludedIngredients,
  onIncludedChange,
  onExcludedChange,
  onClear,
}: IngredientFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'include' | 'exclude'>('include');

  const filteredIngredients = allIngredients.filter(ing =>
    ing.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleIncluded = (ingredient: string) => {
    if (includedIngredients.includes(ingredient)) {
      onIncludedChange(includedIngredients.filter(i => i !== ingredient));
    } else {
      if (excludedIngredients.includes(ingredient)) {
        onExcludedChange(excludedIngredients.filter(i => i !== ingredient));
      }
      onIncludedChange([...includedIngredients, ingredient]);
    }
  };

  const toggleExcluded = (ingredient: string) => {
    if (excludedIngredients.includes(ingredient)) {
      onExcludedChange(excludedIngredients.filter(i => i !== ingredient));
    } else {
      if (includedIngredients.includes(ingredient)) {
        onIncludedChange(includedIngredients.filter(i => i !== ingredient));
      }
      onExcludedChange([...excludedIngredients, ingredient]);
    }
  };

  const hasFilters = includedIngredients.length > 0 || excludedIngredients.length > 0;

  return (
    <div className="mb-4">
      {/* Toggle Button - Plus compact */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between p-3 rounded-xl transition-all cursor-pointer ${
          hasFilters 
            ? 'bg-terracotta text-white shadow-md' 
            : 'bg-rose-soft text-chocolate hover:shadow-sm'
        }`}
      >
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
            hasFilters ? 'bg-white/20' : 'bg-cream'
          }`}>
            <i className={`ri-filter-3-line text-base ${hasFilters ? 'text-white' : 'text-terracotta'}`}></i>
          </div>
          <div className="text-left">
            <p className="font-bold text-sm">Filtrer par ingrédients</p>
            <p className={`text-[10px] ${hasFilters ? 'text-white/80' : 'text-dark/50'}`}>
              {hasFilters 
                ? `${includedIngredients.length} inclus · ${excludedIngredients.length} exclus`
                : 'Trouvez les pizzas selon vos goûts'
              }
            </p>
          </div>
        </div>
        <i className={`ri-arrow-down-s-line text-lg transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {/* Filter Panel - Plus compact */}
      {isOpen && (
        <div className="mt-2 bg-rose-soft rounded-xl p-3 border border-rose-soft animate-fadeIn">
          {/* Tabs - Plus petits */}
          <div className="flex gap-1 mb-3 p-0.5 bg-cream rounded-full">
            <button
              onClick={() => setActiveTab('include')}
              className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-full font-medium transition-all cursor-pointer text-xs ${
                activeTab === 'include'
                  ? 'bg-terracotta text-white shadow-sm'
                  : 'text-dark/60 hover:text-chocolate'
              }`}
            >
              <i className="ri-add-circle-line text-xs"></i>
              <span>Avec</span>
              {includedIngredients.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  activeTab === 'include' ? 'bg-white/20' : 'bg-terracotta/20 text-terracotta'
                }`}>
                  {includedIngredients.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('exclude')}
              className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-full font-medium transition-all cursor-pointer text-xs ${
                activeTab === 'exclude'
                  ? 'bg-chocolate text-white shadow-sm'
                  : 'text-dark/60 hover:text-chocolate'
              }`}
            >
              <i className="ri-indeterminate-circle-line text-xs"></i>
              <span>Sans</span>
              {excludedIngredients.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  activeTab === 'exclude' ? 'bg-white/20' : 'bg-chocolate/20 text-chocolate'
                }`}>
                  {excludedIngredients.length}
                </span>
              )}
            </button>
          </div>

          {/* Search - Plus petit */}
          <div className="relative mb-2">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-dark/40 text-sm"></i>
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-cream rounded-lg text-dark placeholder:text-dark/40 focus:outline-none focus:ring-2 focus:ring-terracotta/30 text-xs"
            />
          </div>

          {/* Selected Tags - Plus compact */}
          {(includedIngredients.length > 0 || excludedIngredients.length > 0) && (
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] font-medium text-chocolate">Filtres actifs</p>
                <button
                  onClick={onClear}
                  className="text-[10px] text-terracotta hover:text-terracotta/80 font-medium cursor-pointer flex items-center gap-0.5"
                >
                  <i className="ri-close-circle-line text-xs"></i>
                  Effacer
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                {includedIngredients.map(ing => (
                  <button
                    key={`inc-${ing}`}
                    onClick={() => toggleIncluded(ing)}
                    className="flex items-center gap-1 px-2 py-0.5 bg-terracotta text-white rounded-full text-[10px] font-medium cursor-pointer hover:bg-terracotta/90 transition-colors"
                  >
                    <i className="ri-add-line text-[8px]"></i>
                    {ing}
                    <i className="ri-close-line text-[8px]"></i>
                  </button>
                ))}
                {excludedIngredients.map(ing => (
                  <button
                    key={`exc-${ing}`}
                    onClick={() => toggleExcluded(ing)}
                    className="flex items-center gap-1 px-2 py-0.5 bg-chocolate text-white rounded-full text-[10px] font-medium cursor-pointer hover:bg-chocolate/90 transition-colors"
                  >
                    <i className="ri-subtract-line text-[8px]"></i>
                    {ing}
                    <i className="ri-close-line text-[8px]"></i>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Ingredients Grid - 3 colonnes */}
          <div className="max-h-36 overflow-y-auto pr-1 custom-scrollbar">
            <div className="grid grid-cols-3 gap-1">
              {filteredIngredients.map(ingredient => {
                const isIncluded = includedIngredients.includes(ingredient);
                const isExcluded = excludedIngredients.includes(ingredient);
                
                return (
                  <button
                    key={ingredient}
                    onClick={() => activeTab === 'include' ? toggleIncluded(ingredient) : toggleExcluded(ingredient)}
                    className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap truncate ${
                      isIncluded
                        ? 'bg-terracotta text-white shadow-sm'
                        : isExcluded
                        ? 'bg-chocolate text-white shadow-sm'
                        : 'bg-cream text-dark/70 hover:bg-cream/80 hover:text-chocolate'
                    }`}
                    title={ingredient}
                  >
                    <span className="flex items-center justify-center gap-0.5">
                      {isIncluded && <i className="ri-add-line text-[8px]"></i>}
                      {isExcluded && <i className="ri-subtract-line text-[8px]"></i>}
                      {ingredient}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {filteredIngredients.length === 0 && (
            <div className="text-center py-4">
              <i className="ri-search-line text-2xl text-dark/20 mb-1"></i>
              <p className="text-dark/50 text-xs">Aucun ingrédient trouvé</p>
            </div>
          )}

          {/* Help Text - Plus compact */}
          <div className="mt-2 pt-2 border-t border-cream">
            <div className="flex items-center gap-2 text-[10px] text-dark/60">
              <i className="ri-lightbulb-line text-terracotta text-sm"></i>
              <p>
                {activeTab === 'include' 
                  ? 'Sélectionnez les ingrédients que vous aimez.'
                  : 'Sélectionnez les ingrédients à éviter.'
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
